<template>
    <div class="vip-status-container">
        <div class="vip-status-card">
            <!-- Logo区域 -->
            <div class="logo-header">
                <div class="logo">
                    <img src="../assets/Logo.png" style="width: 100px;">
                </div>
                <h2>悦览PRO状态查询</h2>
            </div>

            <!-- 查询表单 -->
            <div class="query-form">
                <div class="form-group">
                    <label for="unionId">UnionID</label>
                    <input 
                        type="text" 
                        id="unionId" 
                        v-model="unionId" 
                        placeholder="请输入您的UnionID"
                        :class="{ 'error': unionIdError }"
                        @keyup.enter="checkVipStatus"
                    />
                    <div v-if="unionIdError" class="error-message">{{ unionIdError }}</div>
                    
                    <div class="help-section">
                        <div class="help-title">💡 如何获取UnionID</div>
                        <div class="help-steps">
                            <div class="step">1. 打开悦览应用</div>
                            <div class="step">2. 进入「设置」→「账号管理」</div>
                            <div class="step">3. 点击头像复制 UnionID</div>
                        </div>
                    </div>
                </div>

                <button 
                    class="query-btn" 
                    @click="checkVipStatus" 
                    :disabled="loading || !unionId.trim()"
                >
                    <div v-if="loading" class="loading-spinner"></div>
                    {{ loading ? '查询中...' : '查询悦览PRO状态' }}
                </button>
            </div>

            <!-- 查询结果 -->
            <div v-if="queryResult" class="result-section">
                <!-- 悦览PRO有效状态 -->
                <div v-if="queryResult.success && queryResult.data && queryResult.data.is_vip" class="result-card success">
                    <div class="result-icon">✅</div>
                    <h3>悦览PRO状态有效</h3>
                    
                    <!-- 永久会员ID铭牌 -->
                    <div v-if="isPermanentMember(queryResult.data)" class="vip-id-card">
                        <div class="card-background">
                            <div class="card-shine"></div>
                            <div class="card-content">
                                <div class="id-section">
                                    <div class="id-label">会员ID</div>
                                    <div class="id-value">{{ formatVipId(queryResult.data.id) }}</div>
                                </div>
                                <div class="card-footer">
                                    <div class="hologram">◆ 悦览PRO ◆</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="vip-info">
                        <div class="info-row">
                            <span class="label">悦览PRO类型：</span>
                            <span class="value">{{ getVipTypeName(queryResult.data.vip_type) }}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">开始时间：</span>
                            <span class="value">{{ formatDateTime(queryResult.data.datetime_start) }}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">到期时间：</span>
                            <span class="value" :class="{ 'permanent': isPermanent(queryResult.data.datetime_end) }">
                                {{ formatDateTime(queryResult.data.datetime_end) }}
                            </span>
                        </div>
                        <div class="info-row">
                            <span class="label">状态：</span>
                            <span class="value status-active">{{ queryResult.vip_status }}</span>
                        </div>
                    </div>
                    
                    <div class="activation-tip">
                        <span class="tip-icon">💡</span>
                        <span class="tip-text">购买后请<strong>彻底关闭并重启悦览APP</strong>以激活会员权益</span>
                    </div>
                    
                    <!-- 永久会员QQ群信息 -->
                    <div v-if="isPermanentMember(queryResult.data)" class="qq-group-info">
                        <div class="group-header">
                            <div class="group-icon">🎉</div>
                            <h4>悦览PRO永久会员俱乐部</h4>
                        </div>
                        <div class="group-content">
                            <p>恭喜您成为永久会员！欢迎加入我们的专属QQ群，与其他永久会员交流分享。</p>
                            <div class="qq-info">
                                <span class="qq-label">QQ群号：</span>
                                <span class="qq-number">850144568</span>
                                <button class="copy-btn" @click="copyQQGroup">复制</button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 付款成功但未发放权益 -->
                <div v-else-if="queryResult.message === '付款成功但由于系统原因未发放权益'" class="result-card error">
                    <div class="result-icon">❌</div>
                    <h3>权益发放异常</h3>
                    <p>您的付款已成功，但由于系统原因暂未发放悦览PRO权益。</p>
                    <div class="contact-info">
                        <p>请联系客服处理：</p>
                        <div class="wechat-info">
                            <span class="wechat-label">微信号：</span>
                            <span class="wechat-id">NIGHTLUKA_</span>
                            <button class="copy-btn" @click="copyWechatId">复制</button>
                        </div>
                    </div>
                </div>

                <!-- 无悦览PRO记录 (特定消息) -->
                <div v-else-if="queryResult.message === '用户无VIP记录且无付款记录'" class="result-card warning">
                    <div class="result-icon">⚠️</div>
                    <h3>暂无悦览PRO记录</h3>
                    <p>您还没有购买过悦览PRO服务，或者没有相关的付款记录。</p>
                    <button class="action-btn" @click="goToPurchase">立即购买悦览PRO</button>
                </div>

                <!-- 查询成功但非VIP (兜底 success=true) -->
                <div v-else-if="queryResult.success" class="result-card warning">
                    <div class="result-icon">⚠️</div>
                    <h3>未生效</h3>
                    <p>{{ queryResult.message || '您当前未开通悦览PRO或权益已过期。' }}</p>
                    <button class="action-btn" @click="goToPurchase">立即购买悦览PRO</button>
                </div>

                <!-- 其他错误状态 -->
                <div v-else class="result-card error">
                    <div class="result-icon">❌</div>
                    <h3>查询失败</h3>
                    <p>{{ queryResult.message || '查询悦览PRO状态时发生错误，请稍后重试。' }}</p>
                </div>
            </div>


            <!-- 版权信息 -->
            <div class="footer-copyright">
                <p>© 2025 珠海字符科技有限公司 版权所有</p>
                <p><a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">粤ICP备2025361041号</a></p>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    name: 'VipStatusView',
    data() {
        return {
            unionId: '',
            unionIdError: '',
            loading: false,
            queryResult: null
        }
    },
    mounted() {
        // 从localStorage获取之前输入的unionID
        const savedUnionId = localStorage.getItem('unionId')
        if (savedUnionId) {
            this.unionId = savedUnionId
        }
    },
    methods: {
        validateUnionId() {
            if (!this.unionId.trim()) {
                this.unionIdError = '请输入UnionID'
                return false
            }
            if (this.unionId.length < 10) {
                this.unionIdError = 'UnionID格式不正确'
                return false
            }
            this.unionIdError = ''
            return true
        },

        async checkVipStatus() {
            if (!this.validateUnionId()) {
                return
            }

            this.loading = true
            this.queryResult = null

            try {
                const response = await axios.post('https://api.charactech.cn/yuelan/v2/uservip/check_vip_payment_status', {
                    unionID: this.unionId.trim()
                }, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                })

                const result = response.data
                // 兼容处理：如果后端没返回 success 字段，但 code 为 200，视为成功
                if (result.success === undefined && result.code === 200) {
                    result.success = true
                }
                
                this.queryResult = result
                
                // 保存unionID到localStorage
                localStorage.setItem('unionId', this.unionId.trim())
            } catch (error) {
                console.error('查询悦览PRO状态失败:', error)
                this.queryResult = {
                    success: false,
                    message: error.response?.data?.message || '网络错误，请稍后重试'
                }
            } finally {
                this.loading = false
            }
        },

        getVipTypeName(vipType) {
            const typeMap = {
                0: '月度悦览PRO',
                1: '半年悦览PRO', 
                2: '年度悦览PRO',
                3: '永久悦览PRO'
            }
            return typeMap[vipType] || '未知类型'
        },

        formatDateTime(dateTimeStr) {
            if (!dateTimeStr) return '未知'
            
            // 检查是否为永久有效
            if (dateTimeStr.includes('2199')) {
                return '永久有效'
            }
            
            try {
                const date = new Date(dateTimeStr)
                return date.toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                })
            } catch (e) {
                return dateTimeStr
            }
        },

        isPermanent(dateTimeStr) {
            return dateTimeStr && dateTimeStr.includes('2199')
        },

        isPermanentMember(vipData) {
            return vipData && vipData.vip_type === 3
        },

        formatVipId(id) {
            // 将ID格式化为4位数字，左边补0
            return String(id).padStart(4, '0')
        },

        copyQQGroup() {
            const qqGroup = '850144568'
            if (navigator.clipboard) {
                navigator.clipboard.writeText(qqGroup).then(() => {
                    alert('QQ群号已复制到剪贴板')
                }).catch(() => {
                    this.fallbackCopy(qqGroup)
                })
            } else {
                this.fallbackCopy(qqGroup)
            }
        },

        copyWechatId() {
            const wechatId = 'NIGHTLUKA_'
            if (navigator.clipboard) {
                navigator.clipboard.writeText(wechatId).then(() => {
                    alert('微信号已复制到剪贴板')
                }).catch(() => {
                    this.fallbackCopy(wechatId)
                })
            } else {
                this.fallbackCopy(wechatId)
            }
        },

        fallbackCopy(text) {
            const textArea = document.createElement('textarea')
            textArea.value = text
            document.body.appendChild(textArea)
            textArea.select()
            try {
                document.execCommand('copy')
                alert('微信号已复制到剪贴板')
            } catch (err) {
                alert('复制失败，请手动复制：' + text)
            }
            document.body.removeChild(textArea)
        },

        goToPurchase() {
            this.$router.push('/membership')
        },

        goBack() {
            this.$router.push('/')
        }
    }
}
</script>

<style scoped>
.vip-status-container {
    min-height: 100vh;
    background: #f8fafc;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.vip-status-card {
    background: white;
    border-radius: 16px;
    padding: 32px;
    max-width: 600px;
    width: 100%;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
}

.logo-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-bottom: 32px;
    padding-bottom: 24px;
    border-bottom: 1px solid #e2e8f0;
}

.logo-header h2 {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 700;
    color: #1e293b;
}

.query-form {
    margin-bottom: 32px;
}

.form-group {
    margin-bottom: 24px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
}

.form-group input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.2s ease;
    box-sizing: border-box;
}

.form-group input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-group input.error {
    border-color: #ef4444;
}

.error-message {
    color: #ef4444;
    font-size: 0.875rem;
    margin-top: 4px;
}

.help-section {
    margin-top: 16px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
    border-left: 4px solid #3b82f6;
}

.help-title {
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 12px;
    font-size: 0.875rem;
}

.help-steps {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.step {
    color: #64748b;
    font-size: 0.875rem;
    line-height: 1.4;
}

.query-btn {
    width: 100%;
    background: #3b82f6;
    color: white;
    border: none;
    padding: 16px 24px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.query-btn:hover:not(:disabled) {
    background: #2563eb;
}

.query-btn:disabled {
    background: #94a3b8;
    cursor: not-allowed;
}

.loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.result-section {
    margin-bottom: 32px;
}

.result-card {
    padding: 24px;
    border-radius: 12px;
    text-align: center;
    border: 1px solid;
}

.result-card.success {
    background: #f0fdf4;
    border-color: #22c55e;
}

.result-card.warning {
    background: #fffbeb;
    border-color: #f59e0b;
}

.result-card.error {
    background: #fef2f2;
    border-color: #ef4444;
}

.result-icon {
    font-size: 3rem;
    margin-bottom: 16px;
}

.result-card h3 {
    margin: 0 0 16px 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
}

.result-card p {
    margin: 0 0 16px 0;
    color: #64748b;
    line-height: 1.6;
}

.vip-info {
    text-align: left;
    background: white;
    padding: 20px;
    border-radius: 8px;
    margin-top: 16px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f1f5f9;
}

.info-row:last-child {
    border-bottom: none;
}

.label {
    font-weight: 500;
    color: #64748b;
}

.value {
    font-weight: 600;
    color: #1e293b;
}

.value.permanent {
    color: #059669;
    font-weight: 700;
}

.status-active {
    color: #059669;
}

.contact-info {
    text-align: left;
    margin-top: 16px;
}

.wechat-info {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    padding: 12px;
    background: #f8fafc;
    border-radius: 8px;
}

.wechat-label {
    font-weight: 500;
    color: #64748b;
}

.wechat-id {
    font-family: monospace;
    font-weight: 600;
    color: #1e293b;
    background: white;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
}

.copy-btn {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.copy-btn:hover {
    background: #2563eb;
}

.action-btn {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 16px;
}

.action-btn:hover {
    background: #2563eb;
}

.actions {
    margin-bottom: 32px;
    text-align: center;
}

.back-btn {
    background: #f8fafc;
    color: #64748b;
    border: 1px solid #e2e8f0;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.back-btn:hover {
    background: #f1f5f9;
    color: #475569;
    border-color: #cbd5e1;
}

.footer-copyright {
    text-align: center;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
    color: #94a3b8;
    font-size: 0.75rem;
    line-height: 1.5;
}

.footer-copyright a {
    color: #94a3b8;
    text-decoration: none;
}

.footer-copyright a:hover {
    color: #64748b;
}

/* VIP ID卡片样式 */
.vip-id-card {
    margin: 24px 0;
    perspective: 1000px;
}

.card-background {
    position: relative;
    width: 100%;
    height: 180px;
    background: linear-gradient(135deg, 
        #1a1a2e 0%, 
        #16213e 25%, 
        #0f3460 50%, 
        #16213e 75%, 
        #1a1a2e 100%);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.3),
        0 2px 8px rgba(0, 0, 0, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
    transform-style: preserve-3d;
    transition: transform 0.3s ease;
}

.card-background:hover {
    transform: rotateY(5deg) rotateX(5deg);
}

.card-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
        transparent, 
        rgba(255, 255, 255, 0.2), 
        transparent);
    animation: shine 3s infinite;
}

@keyframes shine {
    0% { left: -100%; }
    50% { left: 100%; }
    100% { left: 100%; }
}

.card-content {
    position: relative;
    height: 100%;
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    z-index: 2;
}

.card-header {
    display: flex;
    justify-content: center;
    align-items: center;
}

.vip-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: rgba(255, 215, 0, 0.2);
    border: 1px solid rgba(255, 215, 0, 0.5);
    border-radius: 20px;
    backdrop-filter: blur(10px);
}

.crown {
    font-size: 1.2rem;
    filter: drop-shadow(0 0 8px rgba(255, 215, 0, 0.8));
}

.badge-text {
    font-size: 0.75rem;
    font-weight: 700;
    color: #ffd700;
    letter-spacing: 1px;
    text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
}

.id-section {
    text-align: center;
    margin: 16px 0;
}

.id-label {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 8px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

.id-value {
    font-family: 'Courier New', 'Monaco', monospace;
    font-size: 2.5rem;
    font-weight: 900;
    color: #ffffff;
    text-shadow: 
        0 0 20px rgba(255, 255, 255, 0.5),
        0 0 40px rgba(135, 206, 250, 0.3);
    letter-spacing: 4px;
    background: linear-gradient(45deg, #ffffff, #87ceeb, #ffffff);
    background-size: 200% 200%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: gradient-shift 2s ease-in-out infinite;
}

@keyframes gradient-shift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
}

.card-footer {
    text-align: center;
}

.hologram {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
    letter-spacing: 2px;
    text-transform: uppercase;
    opacity: 0.8;
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

/* QQ群信息样式 */
.qq-group-info {
    margin-top: 20px;
    padding: 20px;
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    border-radius: 12px;
    border: 1px solid #f59e0b;
}

.group-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}

.group-icon {
    font-size: 1.5rem;
}

.group-header h4 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #92400e;
}

.group-content p {
    margin: 0 0 12px 0;
    color: #92400e;
    font-size: 0.9375rem;
    line-height: 1.5;
}

.qq-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 8px;
    border: 1px solid rgba(245, 158, 11, 0.3);
}

.qq-label {
    font-weight: 500;
    color: #92400e;
}

.qq-number {
    font-family: monospace;
    font-weight: 700;
    color: #1e293b;
    background: white;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    font-size: 1rem;
}

/* 移动端适配 */
@media (max-width: 640px) {
    .card-background {
        height: 160px;
    }
    
    .card-content {
        padding: 16px;
    }
    
    .id-value {
        font-size: 2rem;
        letter-spacing: 2px;
    }
    
    .badge-text {
        font-size: 0.6875rem;
    }
    
    .vip-badge {
        padding: 6px 12px;
    }
    
    .qq-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
    
    .group-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
}

.activation-tip {
    margin-top: 16px;
    padding: 12px;
    background: #eff6ff;
    border-radius: 8px;
    border: 1px solid #bfdbfe;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    text-align: left;
}

.tip-icon {
    font-size: 1.1rem;
}

.tip-text {
    font-size: 0.875rem;
    color: #1e40af;
    line-height: 1.5;
}

.tip-text strong {
    color: #1e3a8a;
    font-weight: 600;
}
</style>